/**
 * @file services/companies.js
 * @since v1.0
 * @author Carlos Lenon <code@carloslenon.com>
 */

// #region constant
// #endregion
// #region common
import { ApiResponse } from 'common/types/response';
import BaseServiceRetryWrapper from './baseRetry';
import { CAPTABLE } from '../common/endpoints';
// #endregion

class CapatableService extends BaseServiceRetryWrapper {
  /**
   * @function
   * @name dataById
   * @description load captable by id selected.
   * @param {INTEGER} dataId the captable id
   */
  dataById = (dataId: string) => this.get(`${CAPTABLE.byId}/${dataId}`);

  /**
   * @function
   * @name getCaptableById
   * @description load captable by id selected.
   * @param {integer} captableId the captable id
   */
  getCaptableById = <T = any>(captableId: number) =>
    this.get(CAPTABLE.captableById(captableId)) as Promise<ApiResponse<T>>;

  /**
   * @function
   * @name updateCaptableById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateCaptableById = (captableId: number, data: unknown) => this.put(CAPTABLE.captableById(captableId), data);

  /**
   * @function
   * @name updateSecuritiesById
   * @description update captable by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateSecuritiesById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateSecurititesById(captableId), data);

  /**
   * @function
   * @name updateFundOwnershipById
   * @description update captable fund ownership by id selected.
   * @param {INTEGER} captableId the captable id
   */
  updateFundOwnershipById = (captableId: number, data: unknown) =>
    this.put(CAPTABLE.updateFundOwnershipById(captableId), data);

  createCapTableProforma = (data: unknown) => this.post(CAPTABLE.createProforma(), data);

  getPrimaryCaptableByCompanyDate = (companyDate: string) =>
    this.get(CAPTABLE.primaryCaptableByCompanyDate(companyDate));

  gotFinalAllocation = (id: number) => this.get(CAPTABLE.gotFinalAllocation(id));

  hasMeasurementDates = (id: number) => this.get(CAPTABLE.hasMeasurementDates(id));

  /**
   * @function
   * @name getAllBreakpoints
   * @description get all breakpoints (waterfall and custom)
   * @param {string} id the captable id
   */
  getAllBreakpointsData = (id: string) => this.get(CAPTABLE.allBreakpointsData(id));

  /**
   * @function
   * @name updateCustomBreakpoints
   * @description update custom breakpoints by cap table id.
   * @param {string} captableId the captable id
   */
  updateCustomBreakpoints = (captableId: string, data: unknown) =>
    this.put(CAPTABLE.allBreakpointsData(captableId), data);

  /**
   * @function
   * @name reviewCitation
   * @description accept or (soft, reversibly) dismiss a single citation reference
   * (SCAL-8963) - the citation-level settle for review-panel items that carry no
   * ai_finding field metadata to settle through.
   * @param {number} citationId the citation reference id
   * @param {string} action 'accept' | 'dismiss'
   */
  reviewCitation = (citationId: number, action: 'accept' | 'dismiss') =>
    this.patch(CAPTABLE.citationReviewById(citationId), { action });
}

export default CapatableService;
